import { useEffect, useRef } from "react";
import { useAdaptiveFanProfile } from "@/lib/adaptiveFan/adaptiveFanProfile";
import { derivePrestige } from "@/lib/globalDominance/globalPrestigeStates";
import { trackEvent } from "@/lib/audit";

/**
 * Global Prestige Badge — single compact line that surfaces the user's
 * current elite state, derived from existing AdaptiveFanProfile counters.
 * No I/O, no engines.
 */
export function GlobalPrestigeBadge({ className = "" }: { className?: string }) {
  const profile = useAdaptiveFanProfile();
  const badge = derivePrestige(profile);
  const last = useRef<string>("");

  useEffect(() => {
    if (last.current === badge.state) return;
    last.current = badge.state;
    void trackEvent("global_prestige_badge_shown", {
      state: badge.state,
      weight: badge.weight,
    });
  }, [badge.state, badge.weight]);

  return (
    <div
      className={`relative flex items-center gap-2 overflow-hidden rounded-xl border border-border/60 bg-background/70 px-3 py-2 backdrop-blur-md ${className}`}
      style={{ boxShadow: `0 0 0 1px color-mix(in oklch, ${badge.color} 22%, transparent)` }}
      aria-label={`Global prestige: ${badge.state}`}
    >
      <div
        aria-hidden
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-lg"
        style={{ background: `color-mix(in oklch, ${badge.color} 18%, transparent)` }}
      >
        {badge.emoji}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="text-[10px] font-black uppercase tracking-[0.2em]"
          style={{ color: badge.color }}
        >
          Orgullo de la afición
        </div>
        <div className="truncate text-sm font-black">{badge.state}</div>
        <div className="truncate text-[10px] text-muted-foreground">{badge.tagline}</div>
      </div>
      <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
        {Math.round(badge.weight * 100)}%
      </span>
    </div>
  );
}